import { createClient, type RealtimeChannel, type SupabaseClient } from "@supabase/supabase-js";
import type {
  ArticleRecord,
  AutomaticPublishSettings,
  Category,
  FeaturedArticleSettings,
  MediaAssetRecord,
  MediaOrphan,
  PageRecord,
  PreflightReport,
  PublicationPlan,
  PublicationReport,
  PublishConfig,
  ScheduledPublishJob,
  SiteConfig
} from "../domain/types";
import type { AnalyticsEvent } from "../services/supabase";
import type { GeneratedArticleContent } from "../services/openai-article-generator";

interface Bootstrap {
  configured: boolean;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

interface StudioAuthState {
  configured: boolean;
  contentMode: "local" | "supabase";
  authenticated: boolean;
  passwordRecovery?: boolean;
  email?: string;
  workspaceId?: string;
  workspaces: Array<{ id: string; name: string; slug: string; role: string }>;
}

interface TeamMember {
  userId: string;
  email: string;
  displayName: string;
  role: "admin" | "editor" | "author" | "viewer";
  createdAt: string;
  currentUser: boolean;
}

interface OpenAISettingsState {
  configured: boolean;
  model: string;
  prompt: string;
  defaultPrompt: string;
}

const workspaceStorageKey = "lbstudio-web-workspace";
const contentListeners = new Set<() => void>();
let client: SupabaseClient | undefined;
let bootstrap: Bootstrap | undefined;
let contentChannel: RealtimeChannel | undefined;
let previewUrl: string | undefined;
let passwordRecovery = ["invite", "recovery"].includes(
  new URLSearchParams(window.location.hash.slice(1)).get("type") || ""
);

const ready = (async (): Promise<void> => {
  const response = await fetch("/api/bootstrap", { cache: "no-store" });
  if (!response.ok) throw new Error("Le serveur LBStudio ne répond pas.");
  bootstrap = await response.json() as Bootstrap;
  if (bootstrap.configured) {
    client = createClient(bootstrap.supabaseUrl, bootstrap.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
    client.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") passwordRecovery = true;
    });
  }
})();

function activeWorkspaceId(): string | undefined {
  return localStorage.getItem(workspaceStorageKey) || undefined;
}

async function sessionToken(): Promise<string> {
  await ready;
  if (!client) throw new Error("Supabase n’est pas configuré sur le serveur.");
  const { data, error } = await client.auth.getSession();
  if (error) throw error;
  if (!data.session) throw new Error("Connectez-vous pour continuer.");
  return data.session.access_token;
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
  workspaceRequired = true
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${await sessionToken()}`);
  if (workspaceRequired) {
    const workspaceId = activeWorkspaceId();
    if (!workspaceId) throw new Error("Sélectionnez un workspace.");
    headers.set("X-Workspace-Id", workspaceId);
  }
  let body = init.body;
  if ("json" in init) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const response = await fetch(`/api${path}`, { ...init, headers, body, cache: "no-store" });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => undefined) as { error?: string } | undefined;
    throw new Error(errorBody?.error || `La requête a échoué (HTTP ${response.status}).`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function subscribeToWorkspace(workspaceId: string | undefined): Promise<void> {
  await ready;
  if (!client) return;
  if (contentChannel) {
    await client.removeChannel(contentChannel);
    contentChannel = undefined;
  }
  if (!workspaceId) return;
  let channel = client.channel(`studio-web-content-${workspaceId}`);
  for (const table of ["articles", "pages", "categories", "site_settings", "featured_article_settings", "media_assets"]) {
    channel = channel.on(
      "postgres_changes",
      { event: "*", schema: "public", table, filter: `workspace_id=eq.${workspaceId}` },
      () => contentListeners.forEach((listener) => listener())
    );
  }
  contentChannel = channel.subscribe();
}

async function getAuthState(): Promise<StudioAuthState> {
  await ready;
  if (!bootstrap?.configured || !client) {
    return { configured: false, contentMode: "supabase", authenticated: false, workspaces: [] };
  }
  const { data } = await client.auth.getSession();
  if (!data.session) {
    return { configured: true, contentMode: "supabase", authenticated: false, workspaces: [] };
  }
  const state = await request<StudioAuthState>("/auth/state", {}, false);
  const stored = activeWorkspaceId();
  const workspaceId = state.workspaces.some((workspace) => workspace.id === stored)
    ? stored
    : state.workspaces[0]?.id;
  if (workspaceId) localStorage.setItem(workspaceStorageKey, workspaceId);
  else localStorage.removeItem(workspaceStorageKey);
  await subscribeToWorkspace(workspaceId);
  return { ...state, workspaceId, passwordRecovery };
}

const api = {
  getAuthState,
  async signIn(email: string, password: string): Promise<StudioAuthState> {
    await ready;
    if (!client) throw new Error("Supabase n’est pas configuré sur le serveur.");
    const { error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return getAuthState();
  },
  async signOut(): Promise<StudioAuthState> {
    await ready;
    if (client && contentChannel) await client.removeChannel(contentChannel);
    contentChannel = undefined;
    localStorage.removeItem(workspaceStorageKey);
    if (client) {
      const { error } = await client.auth.signOut();
      if (error) throw error;
    }
    return {
      configured: Boolean(bootstrap?.configured),
      contentMode: "supabase",
      authenticated: false,
      workspaces: []
    };
  },
  async requestPasswordReset(email: string): Promise<void> {
    await ready;
    if (!client) throw new Error("Supabase n’est pas configuré sur le serveur.");
    const { error } = await client.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/`
    });
    if (error) throw error;
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await ready;
    if (!client) throw new Error("Supabase n’est pas configuré sur le serveur.");
    const { data: userData, error: userError } = await client.auth.getUser();
    if (userError || !userData.user?.email) throw new Error("Session Supabase expirée. Reconnectez-vous.");
    const { error: signInError } = await client.auth.signInWithPassword({
      email: userData.user.email,
      password: currentPassword
    });
    if (signInError) throw new Error("Le mot de passe actuel est incorrect.");
    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) throw error;
    const { error: signOutError } = await client.auth.signOut({ scope: "others" });
    if (signOutError) throw signOutError;
  },
  async completePasswordRecovery(newPassword: string): Promise<StudioAuthState> {
    await ready;
    if (!client || !passwordRecovery) throw new Error("Le lien de réinitialisation n’est pas valide ou a expiré.");
    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) throw error;
    const { error: signOutError } = await client.auth.signOut({ scope: "others" });
    if (signOutError) throw signOutError;
    passwordRecovery = false;
    window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
    return getAuthState();
  },
  async createWorkspace(name: string, slug: string): Promise<string> {
    const result = await request<{ id: string }>("/workspaces", {
      method: "POST",
      json: { name, slug }
    }, false);
    return result.id;
  },
  async updateWorkspace(name: string, slug: string): Promise<void> {
    await request("/workspace", {
      method: "PATCH",
      json: { name, slug }
    });
  },
  async selectWorkspace(workspaceId: string): Promise<StudioAuthState> {
    await request(`/workspaces/${encodeURIComponent(workspaceId)}/select`, { method: "POST", json: {} }, false);
    localStorage.setItem(workspaceStorageKey, workspaceId);
    await subscribeToWorkspace(workspaceId);
    return getAuthState();
  },
  listTeamMembers: (): Promise<TeamMember[]> => request("/team"),
  inviteTeamMember: (input: { email: string; displayName?: string; role: TeamMember["role"] }): Promise<TeamMember[]> =>
    request("/team", { method: "POST", json: input }),
  sendTeamMemberPasswordReset: (userId: string): Promise<void> =>
    request(`/team/${encodeURIComponent(userId)}/password-reset`, { method: "POST", json: {} }),
  updateTeamMemberRole: (userId: string, role: TeamMember["role"]): Promise<TeamMember[]> =>
    request(`/team/${encodeURIComponent(userId)}/role`, { method: "PATCH", json: { role } }),
  removeTeamMember: (userId: string): Promise<TeamMember[]> =>
    request(`/team/${encodeURIComponent(userId)}`, { method: "DELETE" }),
  getConfig: (): Promise<SiteConfig> => request("/config"),
  saveConfig: (config: SiteConfig): Promise<SiteConfig> => request("/config", { method: "PUT", json: config }),
  getOpenAISettings: (): Promise<OpenAISettingsState> => request("/openai/settings"),
  saveOpenAISettings: (input: { apiKey?: string; model?: string; prompt?: string; clearApiKey?: boolean }): Promise<OpenAISettingsState> =>
    request("/openai/settings", { method: "PUT", json: input }),
  generateArticleWithAI: (theme: string, prompt: string): Promise<GeneratedArticleContent> =>
    request("/openai/generate", { method: "POST", json: { theme, prompt } }),
  listAnalyticsEvents: (): Promise<AnalyticsEvent[]> => request("/analytics"),
  testAnalyticsConnection: (): Promise<{ ok: boolean; count: number }> =>
    request("/analytics/test", { method: "POST", json: {} }),
  listCategories: (): Promise<Category[]> => request("/categories"),
  saveCategory: (category: Category): Promise<Category[]> =>
    request(`/categories/${encodeURIComponent(category.id)}`, { method: "PUT", json: category }),
  async deleteCategory(id: string): Promise<Category[]> {
    await request(`/categories/${encodeURIComponent(id)}`, { method: "DELETE" });
    return request("/categories");
  },
  listPages: (): Promise<PageRecord[]> => request("/pages"),
  listArticles: (): Promise<ArticleRecord[]> => request("/articles"),
  getFeaturedArticleSettings: (): Promise<FeaturedArticleSettings> => request("/featured-article"),
  saveFeaturedArticleSettings: (settings: FeaturedArticleSettings): Promise<FeaturedArticleSettings> =>
    request("/featured-article", { method: "PUT", json: settings }),
  getPublishConfig: (): Promise<PublishConfig> => request("/publish/config"),
  savePublishConfig: (config: PublishConfig): Promise<PublishConfig> =>
    request("/publish/config", { method: "PUT", json: config }),
  getAutomaticPublishSettings: (): Promise<AutomaticPublishSettings> => request("/publish/automatic"),
  saveAutomaticPublishSettings: (input: {
    enabled: boolean;
    profileId: string;
    password?: string;
    passphrase?: string;
  }): Promise<AutomaticPublishSettings> => request("/publish/automatic", { method: "PUT", json: input }),
  deleteAutomaticPublishSecret: (profileId: string): Promise<void> =>
    request(`/publish/automatic/secret/${encodeURIComponent(profileId)}`, { method: "DELETE" }),
  listScheduledPublishJobs: (): Promise<ScheduledPublishJob[]> => request("/publish/jobs"),
  retryScheduledPublishJob: (id: string): Promise<void> =>
    request(`/publish/jobs/${encodeURIComponent(id)}/retry`, { method: "POST", json: {} }),
  runPreflight: (profileId?: string): Promise<PreflightReport> =>
    request("/preflight", { method: "POST", json: { profileId } }),
  planPublishProfile: (input: {
    profileId: string;
    secrets: { password?: string; passphrase?: string };
  }): Promise<PublicationPlan> => request("/publish/plan", { method: "POST", json: input }),
  pushPublishProfile: (input: {
    profileId: string;
    secrets: { password?: string; passphrase?: string };
    confirmWarnings?: boolean;
  }): Promise<PublicationReport> => request("/publish/push", { method: "POST", json: input }),
  listPublicationHistory: (profileId: string): Promise<PublicationReport[]> =>
    request(`/publish/history/${encodeURIComponent(profileId)}`),
  restorePublication: (input: {
    profileId: string;
    publicationId: string;
    secrets: { password?: string; passphrase?: string };
  }): Promise<PublicationReport> => request("/publish/restore", { method: "POST", json: input }),
  getArticle: (id: string): Promise<ArticleRecord | undefined> =>
    request(`/articles/${encodeURIComponent(id)}`),
  createArticle: (): Promise<ArticleRecord> => request("/articles/new", { method: "POST", json: {} }),
  saveArticle: (article: ArticleRecord): Promise<ArticleRecord> =>
    request(`/articles/${encodeURIComponent(article.id)}`, { method: "PUT", json: article }),
  duplicateArticle: (id: string): Promise<ArticleRecord> =>
    request(`/articles/${encodeURIComponent(id)}/duplicate`, { method: "POST", json: {} }),
  archiveArticle: (id: string): Promise<ArticleRecord> =>
    request(`/articles/${encodeURIComponent(id)}/archive`, { method: "POST", json: {} }),
  async deleteArticle(id: string): Promise<boolean> {
    await request(`/articles/${encodeURIComponent(id)}`, { method: "DELETE" });
    return true;
  },
  createPage: (): Promise<PageRecord> => request("/pages/new", { method: "POST", json: {} }),
  savePage: (page: PageRecord): Promise<PageRecord> =>
    request(`/pages/${encodeURIComponent(page.id)}`, { method: "PUT", json: page }),
  async deletePage(id: string): Promise<boolean> {
    await request(`/pages/${encodeURIComponent(id)}`, { method: "DELETE" });
    return true;
  },
  async saveMediaAsset(fileName: string, bytes: Uint8Array, contentType?: string): Promise<string> {
    const headers = new Headers({
      Authorization: `Bearer ${await sessionToken()}`,
      "X-Workspace-Id": activeWorkspaceId() || "",
      "X-File-Name": encodeURIComponent(fileName),
      "Content-Type": contentType || "application/octet-stream"
    });
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const response = await fetch("/api/media", { method: "POST", headers, body: buffer });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => undefined) as { error?: string } | undefined;
      throw new Error(errorBody?.error || `L’import a échoué (HTTP ${response.status}).`);
    }
    return ((await response.json()) as { publicPath: string }).publicPath;
  },
  listMediaAssets: (): Promise<MediaAssetRecord[]> => request("/media"),
  findMediaOrphans: (): Promise<MediaOrphan[]> => request("/media/orphans"),
  async deleteMediaAsset(id: string): Promise<boolean> {
    await request(`/media/${encodeURIComponent(id)}`, { method: "DELETE" });
    return true;
  },
  async deleteMediaOrphan(orphan: MediaOrphan): Promise<boolean> {
    await request("/media/orphans/delete", { method: "POST", json: orphan });
    return true;
  },
  buildSite: (): Promise<unknown> => request("/build", { method: "POST", json: {} }),
  async startPreview(): Promise<string> {
    const result = await request<{ url: string }>("/preview", { method: "POST", json: {} });
    previewUrl = result.url;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
    return previewUrl;
  },
  async openMarkdownPreview(title: string, html: string): Promise<boolean> {
    const preview = window.open("", "_blank");
    if (!preview) throw new Error("Le navigateur a bloqué la fenêtre d’aperçu.");
    preview.opener = null;
    const safeTitle = title
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
    preview.document.open();
    preview.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${safeTitle}</title><style>body{margin:0;background:#f5f4ef;color:#111;font:17px/1.65 Georgia,serif}main{width:min(760px,calc(100% - 48px));margin:32px auto;padding:32px;background:#fff;border:1px solid #ddd}img{max-width:100%;height:auto}</style></head><body><main><h1>${safeTitle}</h1>${html}</main></body></html>`);
    preview.document.close();
    return true;
  },
  async stopPreview(): Promise<boolean> {
    previewUrl = undefined;
    return true;
  },
  async previewStatus(): Promise<{ running: boolean; url?: string }> {
    return { running: Boolean(previewUrl), url: previewUrl };
  },
  async openExternal(url: string): Promise<boolean> {
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  },
  async pickFolder(): Promise<string | undefined> {
    return undefined;
  },
  onContentChanged(callback: () => void): () => void {
    contentListeners.add(callback);
    return () => contentListeners.delete(callback);
  }
};

(window as unknown as { lesBoulesStudio: typeof api }).lesBoulesStudio = api;
